import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { AgentOrchestratorService } from '../../services/agent-orchestrator/agent-orchestrator.service';
import { AgentConfigService } from '../../services/agent-config/agent-config.service';
import { AgentConfig, AgentHubItem } from '../../models';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { SessionThread } from '../../../../shared/models/session.models';
import { StreamingStateService } from '../../../../shared/services/streaming-state/streaming-state.service';

/**
 * Agent Header Component
 * 
 * Displays agent-specific header with agent name, icon, and dropdown menu.
 * Provides access to agent actions like "New Chat" and "About".
 * 
 * Features:
 * - Shows current agent name and icon
 * - Dropdown menu for agent actions
 * - Responds to agent changes via orchestrator
 * - Adapts layout when sidebar is open/closed
 * - New Chat dropdown with agent info, New Topic, and recent conversations
 * 
 * Usage:
 * <app-agent-header
 *   [agentId]="currentAgentId"
 *   [isSidebarOpen]="sidebarOpen"
 *   (newChatRequested)="onNewChat()"
 *   (sessionSelected)="onSessionSelected($event)"
 *   (aboutRequested)="onAbout()">
 * </app-agent-header>
 */
@Component({
  selector: 'app-agent-header',
  templateUrl: './agent-header.component.html',
  styleUrls: ['./agent-header.component.scss']
})
export class AgentHeaderComponent implements OnInit, OnDestroy {
  @Input() agentId: string = '';
  @Input() isSidebarOpen: boolean = false;
  /** When true (view all sources panel open), New Topic / dropdown content is not shown */
  @Input() isSourcePanelVisible: boolean = false;
  @Input() isNewChat: boolean = true;
  @Output() newChatRequested = new EventEmitter<void>();
  @Output() aboutRequested = new EventEmitter<void>();
  @Output() navigateToHubRequested = new EventEmitter<void>();
  @Output() sessionSelected = new EventEmitter<any>();

  showAgentDropdown: boolean = false;
  showNewChatDropdown: boolean = false;
  currentAgentConfig: AgentConfig | null = null;

  /** Latest 2 threads for the dropdown */
  latestThreads: SessionThread[] = [];

  /** Current thread ID so header dropdown can highlight the selected session */
  currentThreadId: string | null = null;

  /** Info popup state */
  showInfoPopup: boolean = false;
  selectedAgentForInfo: AgentHubItem | null = null;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly agentOrchestrator: AgentOrchestratorService,
    private readonly agentConfigService: AgentConfigService,
    private readonly logger: LoggerService,
    private readonly elementRef: ElementRef,
    private readonly streamingStateService: StreamingStateService
  ) {}

  /**
   * Initialize component and subscribe to agent changes
   */
  ngOnInit(): void {
    try {
      const agentSub = this.agentOrchestrator.currentAgentId$.subscribe((agentId: string) => {
        this.agentId = agentId;
        if (agentId && agentId !== 'module') {
          this.currentAgentConfig = this.agentConfigService.getAgentConfig(agentId) || null;
        } else {
          this.currentAgentConfig = null;
        }
      });
      this.subscriptions.push(agentSub);

      const threadSub = this.agentOrchestrator.currentThreadId$.subscribe((threadId: string | null) => {
        this.currentThreadId = threadId;
      });
      this.subscriptions.push(threadSub);
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error in ngOnInit', 'ngOnInit', error);
    }
  }

  /**
   * Cleanup subscriptions on component destroy
   */
  ngOnDestroy(): void {
    try {
      this.subscriptions.forEach(sub => sub.unsubscribe());
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error in ngOnDestroy', 'ngOnDestroy', error);
    }
  }

  /**
   * Toggle agent dropdown menu
   * 
   * @param event - Click event
   */
  toggleAgentDropdown(event: Event): void {
    try {
      event.stopPropagation();
      this.showAgentDropdown = !this.showAgentDropdown;
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error toggling dropdown', 'toggleAgentDropdown', error);
    }
  }

  /**
   * Toggle the new chat dropdown popup
   * Loads latest 2 threads from cached data
   * 
   * @param event - Click event
   */
  toggleNewChatDropdown(event: Event): void {
    try {
      event.stopPropagation();
      this.showNewChatDropdown = !this.showNewChatDropdown;

      if (this.showNewChatDropdown) {
        this.loadLatestThreads();
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error toggling new chat dropdown', 'toggleNewChatDropdown', error);
    }
  }

  /**
   * Load the latest 2 threads from the orchestrator cache
   */
  private loadLatestThreads(): void {
    try {
      const cachedThreads = this.agentOrchestrator.getCachedAgentThreads(this.agentId);
      if (cachedThreads && cachedThreads.length > 0) {
        this.latestThreads = cachedThreads.slice(0, 2);
      } else {
        this.latestThreads = [];
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error loading latest threads', 'loadLatestThreads', error);
      this.latestThreads = [];
    }
  }

  /**
   * Handle "New Chat" action from dropdown
   * Closes dropdown and emits event to parent
   */
  onAgentNewChat(): void {
    try {
      this.showAgentDropdown = false;
      this.showNewChatDropdown = false;
      this.newChatRequested.emit();
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error handling new chat', 'onAgentNewChat', error);
    }
  }

  /** Handle keydown on new-chat icon (Enter/Space to trigger new chat). */
  onNewChatIconKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onAgentNewChat();
    }
  }

  /**
   * Handle thread click from the dropdown
   * Emits the selected thread to parent for loading
   * 
   * @param thread - Selected session thread
   */
  onThreadClick(thread: SessionThread): void {
    try {
      this.showNewChatDropdown = false;
      this.sessionSelected.emit(thread);
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error handling thread click', 'onThreadClick', error);
    }
  }

  /**
   * Handle info icon click - opens agent info popup
   */
  onInfoClick(event: Event): void {
    try {
      event.stopPropagation();
      if (this.currentAgentConfig) {
        this.selectedAgentForInfo = this.buildAgentHubItem(this.currentAgentConfig);
        this.showInfoPopup = true;
        this.showNewChatDropdown = false;
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error handling info click', 'onInfoClick', error);
    }
  }

  /**
   * Build an AgentHubItem from AgentConfig for the info popup
   */
  private buildAgentHubItem(config: AgentConfig): AgentHubItem {
    return {
      id: config.id,
      name: config.display_name || config.name,
      description: config.landing_page?.intro || '',
      icon: config.icon,
      category: {
        id: config.category || 'general',
        name: config.category || 'General',
        colorVariant: 'blue'
      },
      about: config.about,
      version: config.version,
      versionNotes: config.versionNotes,
      features: config.features,
      enabled: config.enabled
    };
  }

  /**
   * Handle info popup close
   */
  onInfoPopupClosed(): void {
    this.showInfoPopup = false;
    this.selectedAgentForInfo = null;
  }

  /**
   * Handle "Use" button click from info popup
   */
  onUseAgent(agentId: string): void {
    try {
      this.showInfoPopup = false;
      this.selectedAgentForInfo = null;
      // Already on this agent, just close
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error handling use agent', 'onUseAgent', error);
    }
  }

  /**
   * Handle "Make it default agent" click from info popup
   */
  onMakeDefaultAgent(agentId: string): void {
    try {
      this.showInfoPopup = false;
      this.selectedAgentForInfo = null;
      this.logger.logInfo(`[AgentHeader] Make default agent: ${agentId}`, 'onMakeDefaultAgent');
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error handling make default', 'onMakeDefaultAgent', error);
    }
  }

  /**
   * Handle "About" action from dropdown
   * Closes dropdown and emits event to parent
   */
  onAgentAbout(): void {
    try {
      this.showAgentDropdown = false;
      this.aboutRequested.emit();
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error handling about', 'onAgentAbout', error);
    }
  }

  /**
   * Handle navigation to Agent Hub
   * Navigates back to the agent hub showing all agents
   */
  onNavigateToHub(): void {
    try {
      this.showAgentDropdown = false;
      this.showNewChatDropdown = false;
      
      // Check if streaming is active - show confirmation popup before navigating
      if (this.streamingStateService.getCurrentStreamingState()) {
        this.logger.logInfo('[AgentHeader] Streaming active - requesting confirmation before navigating to hub', 'onNavigateToHub');
        this.agentOrchestrator.checkStreamingAndExecute(
          () => {
            this.agentOrchestrator.navigateToAgentHub();
            this.navigateToHubRequested.emit();
          },
          'agent_switch'
        );
        return;
      }
      
      this.agentOrchestrator.navigateToAgentHub();
      this.navigateToHubRequested.emit();
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error navigating to hub', 'onNavigateToHub', error);
    }
  }

  /**
   * Get agent icon source with fallback
   */
  get agentIconSrc(): string {
    return this.currentAgentConfig?.icon || 'assets/icons/artificial-intelligence.svg';
  }

  /**
   * Close dropdowns when clicking outside
   * 
   * @param event - Mouse click event
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    try {
      // Only close if click is outside this component
      if (!this.elementRef.nativeElement.contains(event.target)) {
        this.showAgentDropdown = false;
        this.showNewChatDropdown = false;
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentHeader] Error handling document click', 'onDocumentClick', error);
    }
  }
}
